import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ICustomer, ILocation } from 'src/app/features/customers/models/customers.models';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Subject, debounceTime } from 'rxjs';
import { IPagination } from 'src/app/shared/models/page.model';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { takeUntil } from 'rxjs/operators';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';

@Component({
  selector: 'app-new-catalog-audience-list',
  templateUrl: './new-catalog-audience-list.component.html',
  styleUrls: ['./new-catalog-audience-list.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('expanded', style({ height: '*', opacity: 1 })),
      state(
        'collapsed',
        style({ height: '0px', opacity: 0, overflow: 'hidden', padding: '0px' })
      ),
      transition('expanded <=> collapsed', animate('250ms ease-in-out')),
    ]),
  ],
})
export class NewCatalogAudienceListComponent implements OnInit, OnChanges {
  @Output() selectedCompaniesChanged = new EventEmitter<boolean>();
  @Input() initialAssignedCustomers: ICustomer[] = [];
  @Output() assignedLocationsChange = new EventEmitter<number[]>();
  @Output() selectedCompaniesChange = new EventEmitter<number[]>();
  @Output() deleteLocations = new EventEmitter<number>();
  @Input() selectedCompaniesChangedOut: string = '';
  @Input() isEdit: boolean = false;

  // Pagination inputs
  @Input() companiesMetadata: IPagination | null = null;
  @Input() isLoadingMore: boolean = false;

  // Pagination outputs
  @Output() loadMore = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();

  customers: ICustomer[] = [];
  expandedCompanies: number[] = [];
  selectedCompanies: number[] = [];

  activeSortKey: string | null = null;
  activeSortMode: 'asc' | 'desc' | null = null;
  companiesToDelete: number[] = [];

  // Scroll management
  private scrollSubject = new Subject<Event>();
  private scrollThreshold = 0.9; // Load more when 90% scrolled
  private lastScrollEmit = 0;
  private scrollCooldown = 500; // 500ms between scroll events

  destroy$ = new Subject<void>();
  canCreate = false;
  canEdit = false;

  constructor(
    private store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    this.customers = this.initialAssignedCustomers;

    // Set up scroll debouncing
    this.scrollSubject
      .pipe(debounceTime(100))
      .subscribe((event) => this.handleScroll(event));
    this.store.select(selectCanDo('catalog', 'create')).pipe(takeUntil(this.destroy$)).subscribe((canCreate) => {
      this.canCreate = canCreate;
    });
    this.store.select(selectCanDo('catalog', 'edit')).pipe(takeUntil(this.destroy$)).subscribe((canEdit) => {
      this.canEdit = canEdit;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialAssignedCustomers'] && changes['initialAssignedCustomers'].currentValue) {
      this.customers = [...changes['initialAssignedCustomers'].currentValue];
    }
    if(changes['selectedCompaniesChangedOut'] && changes['selectedCompaniesChangedOut'].currentValue) {
      if(changes['selectedCompaniesChangedOut'].currentValue === 'clear' || changes['selectedCompaniesChangedOut'].currentValue === 'delete') {
        this.selectedCompanies = [];
      }
    }
  }

  emitSelectedLocationsChange() {
    this.selectedCompaniesChanged.emit(this.selectedCompanies.length > 0);
    this.selectedCompaniesChange.emit(this.selectedCompanies);
  }

  // Check if a company is expanded
  isCompanyExpanded(company: any): boolean {
    return this.expandedCompanies.includes(company.id);
  }

  // Check if all locations in all companies are selected
  isAllLocationsSelected(): boolean {
    const allCompanyIds = this.customers.flatMap((company: ICustomer) =>
      company.id!
    );
    return allCompanyIds.length
      ? allCompanyIds.every((id: any) => this.selectedCompanies.includes(id))
      : false;
  }

  // Check if at least one location is selected but not all
  isPartialSelectedAll(): boolean {
    const allCompanyIds = this.customers.flatMap((company: ICustomer) =>
      company.id!
    );
    const selectedCount = allCompanyIds.filter((id: any) =>
      this.selectedCompanies.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < allCompanyIds.length;
  }

  // Check if a company has all its locations selected
  isCompanyFullySelected(companyId: number): boolean {
    return this.selectedCompanies.includes(companyId);
  }

  // Check if a company has some (but not all) of its locations selected
  isCompanyPartiallySelected(companyId: number): boolean {
    const company = this.customers.find(
      (c: { id: number }) => c.id === companyId
    );
    if (!company) return false;
    const locationIds = company.locations!.map((loc: ILocation) => loc.id);
    const selectedCount = locationIds.filter((id: any) =>
      this.selectedCompanies.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < locationIds.length;
  }

  toggleCompanyExpand(company: any): void {
    this.expandedCompanies = this.expandedCompanies || [];
    const index = this.expandedCompanies.indexOf(company.id);
    if (index > -1) {
      this.expandedCompanies.splice(index, 1);
    } else {
      this.expandedCompanies.push(company.id);
    }
  }

  deleteLocation(locationId: number) {
    if (!this.canSubmit) return;
    this.deleteLocations.emit(locationId);
  }

  toggleAllLocations(checked: boolean) {
    if (!this.canSubmit) return;
    const allIds = this.customers.flatMap((c: ICustomer) =>
      c.id!
    );
    this.selectedCompanies = checked ? [...allIds] : [];
    this.emitSelectedLocationsChange();
  }

  toggleCompanyLocations(companyId: number, checked: boolean) {
    if (!this.canSubmit) return;
    this.selectedCompanies = checked ? [...this.selectedCompanies, companyId] : this.selectedCompanies.filter((id) => id !== companyId);
    this.emitSelectedLocationsChange();
  }


  @Output() sortChange = new EventEmitter<{ sortBy: string; sortMode: 'asc' | 'desc' }>();

  sortByKey(key: string, sortDirection: 'asc' | 'desc'): void {
    this.activeSortKey = key;
    this.activeSortMode = sortDirection;
    this.sortChange.emit({ sortBy: key, sortMode: sortDirection });
  }

  /**
   * Handle scroll event with throttling
   */
  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  /**
   * Process scroll event and trigger load more when threshold reached
   */
  private handleScroll(event: Event): void {
    const now = Date.now();

    // Prevent rapid-fire emissions
    if (now - this.lastScrollEmit < this.scrollCooldown) {
      return;
    }

    // Don't trigger if already loading
    if (this.isLoadingMore) {
      return;
    }

    // Don't trigger if no more data
    if (!this.hasMore) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) return;

    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Calculate scroll position as percentage
    const scrollPosition = (scrollTop + clientHeight) / scrollHeight;

    // Trigger load more when scroll threshold reached
    if (scrollPosition > this.scrollThreshold) {
      this.lastScrollEmit = now;
      this.loadMore.emit();
    }
  }

  /**
   * Check if there is more data to load
   */
  get hasMore(): boolean {
    if (!this.companiesMetadata) return false;
    return this.companiesMetadata.page < this.companiesMetadata.pages;
  }

  trackByCustomerId(_index: number, customer: ICustomer): string | number {
    return customer.id!;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canSubmit(): boolean {
    return this.isEdit ? this.canEdit : this.canCreate;
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: this.isEdit ? 'edit' : 'create' };
  }
}